// QuickAuditFull.tsx — internal multi-engine Quick Audit for prospects.
// Admin + Manager only. Runs a ONE-SHOT audit across the selected engines
// (Google Maps grid · AI Visibility · GBP snapshot), polls until done, and keeps
// a History of past runs. NOT tied to a permanent client — see lib/quick-audit.ts.
import { useEffect, useRef, useState, useCallback, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { T, sharedStyles, rankColor } from '../components/Shared';
import { useIsMobile } from '../lib/useIsMobile';

// ── Shapes returned by /api/quick-audit/job/:id ──────────────────────────────
type Engine = 'maps' | 'ai' | 'gbp';
type EngineStatus = 'pending' | 'running' | 'done' | 'error';

interface Candidate { place_id: string; name: string; address: string; lat: number; lng: number; rating: string; reviews: number }

interface MapsGridPoint {
  index: number; lat: number; lng: number; rank: number; found: boolean;
  results: { rank: number; place_id: string; name: string; rating: number | null; reviews: number | null; category: string }[];
}
interface MapsScan {
  keyword: string;
  report_key: string; place_id: string;
  business: { name?: string; address?: string; rating?: string; reviews?: number };
  grid_size: string; radius: string; measurement: string;
  found_in: string; arp: string; atrp: string; solv: string;
  center: { lat: number; lng: number }; grid: MapsGridPoint[];
}
// One Maps engine result holds a scan per keyword. While scans run they sit in
// `pending`; completed ones move into `scans`. Older audits stored a single
// scan inline at the top level — normalizeMaps() handles both shapes.
interface MapsData {
  keywords?: string[];
  scans?: MapsScan[];
  pending?: { keyword: string; report_key: string }[];
  errors?: string[];
  // legacy single-scan fields (pre multi-keyword):
  report_key?: string; keyword?: string; grid?: MapsGridPoint[];
}
interface AiScan {
  keyword: string; platform: string; label: string; mentioned: boolean; rank: number | null;
  competitors: string[]; answer: string; sources: { source?: string; link?: string; title?: string; count: number }[];
}
interface AiData {
  platforms: string[]; keywords: string[]; scans: AiScan[];
  visibility_pct: number; mentioned_count: number; total: number; best_rank: number | null;
}
interface GbpData {
  name: string | null;
  rating: { value: number | null; votes_count: number | null };
  total_reviews: number | null; sample_size: number;
  effective: { pct: number; photo_pct: number; quality_text_pct: number; junk_pct: number; effective_count: number };
  monthly: { ym: string; count: number }[];
  recent: { last_14d: number; last_30d: number };
  star_distribution: Record<string, number> | null;
}
interface EngineResult { engine: Engine; status: EngineStatus; score: number | null; error: string | null; data: unknown; updated_at: number }
interface GbpReportMeta { status: 'pending' | 'ready' | 'error' | 'none'; summary: string | null; score: number | null; error: string | null; generated_at: number | null }
interface AuditJob {
  audit: {
    id: string; business_name: string; place_id: string; address: string | null; location_area: string | null;
    market: string; keyword_language: string; keywords: string[]; engines: Engine[];
    status: 'running' | 'done' | 'partial' | 'error'; created_at: number; updated_at: number;
  };
  results: EngineResult[];
  gbp_report: GbpReportMeta | null;
}
interface HistoryAudit {
  id: string; business_name: string; location_area: string | null; market: string;
  keywords: string[]; engines: Engine[]; status: string; created_at: number;
  results: { engine: Engine; status: EngineStatus; score: number | null }[];
}

const POLL_MS = 5000;
const MAX_POLL_MS = 5 * 60 * 1000;
const REPORT_MAX_POLL_MS = 20 * 60 * 1000; // the full GBP report is a manual skill run
const ENGINE_LABEL: Record<Engine, string> = { maps: 'Google Maps grid', ai: 'AI Visibility', gbp: 'GBP snapshot' };

// ── Google Maps URL parser ────────────────────────────────────────────────────
interface ParsedMapsUrl { placeId: string; lat: number; lng: number }
function isShortMapsUrl(url: string) { return /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(url); }

// Google encodes the place reference two ways in the data= fragment:
//   1. !1sChIJ…                              → the place_id directly
//   2. !1s0x<cellId>:0x<featureId>           → a hex feature id (most shared
//      links use THIS). Convert it to the ChIJ place_id deterministically:
//      the place_id is base64url( 0x0a, len, 0x09 + LE64(cellId) + 0x11 + LE64(featureId) ).
//      Verified to round-trip against live LF place_ids.
function featureIdToPlaceId(highHex: string, lowHex: string): string {
  const le = (hex: string): number[] => {
    let v = BigInt('0x' + hex); const out: number[] = [];
    for (let i = 0; i < 8; i++) { out.push(Number(v & 0xffn)); v >>= 8n; }
    return out;
  };
  const inner = [0x09, ...le(highHex), 0x11, ...le(lowHex)];
  const outer = [0x0a, inner.length, ...inner];
  const bin = String.fromCharCode(...outer);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function parseMapsUrl(url: string): ParsedMapsUrl | null {
  try {
    const decoded = decodeURIComponent(url);
    // coordinates from the @lat,lng part (or the !3d…!4d… pair as a fallback)
    const coordMatch = decoded.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/)
      || decoded.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
    if (!coordMatch) return null;
    const lat = parseFloat(coordMatch[1]), lng = parseFloat(coordMatch[2]);

    // Prefer a direct ChIJ place_id; otherwise convert a hex feature id.
    const chij = decoded.match(/[!&]1s(ChIJ[A-Za-z0-9_-]+)/);
    if (chij) return { placeId: chij[1], lat, lng };
    const hex = decoded.match(/[!&]1s(0x[0-9a-f]+):0x([0-9a-f]+)/i);
    if (hex) return { placeId: featureIdToPlaceId(hex[1].slice(2), hex[2]), lat, lng };
    return null;
  } catch { return null; }
}

type Phase = 'form' | 'searching' | 'picking' | 'auditing' | 'results' | 'error';
type Tab = 'new' | 'history';

export function QuickAuditFull() {
  const [tab, setTab] = useState<Tab>('new');
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [mapsUrl, setMapsUrl] = useState('');
  const [urlParsed, setUrlParsed] = useState<ParsedMapsUrl | null>(null);
  const [urlExpanding, setUrlExpanding] = useState(false);
  const [market, setMarket] = useState<'SG' | 'MY'>('SG');
  const [lang, setLang] = useState<'en' | 'zh' | 'ms'>('en');
  const [keywords, setKeywords] = useState<string[]>(['', '', '']);
  const [engines, setEngines] = useState<Record<Engine, boolean>>({ maps: true, ai: true, gbp: true });

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [job, setJob] = useState<AuditJob | null>(null);

  const pollStart = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (pollTimer.current) clearTimeout(pollTimer.current); }, []);

  // Parse Google Maps URL whenever it changes; expand short links via backend.
  useEffect(() => {
    const raw = mapsUrl.trim();
    if (!raw) { setUrlParsed(null); setUrlExpanding(false); return; }
    if (isShortMapsUrl(raw)) {
      setUrlExpanding(true); setUrlParsed(null);
      fetch('/api/quick-audit/expand-url?' + new URLSearchParams({ url: raw }))
        .then(r => r.json() as Promise<{ expanded?: string }>)
        .then(d => { setUrlExpanding(false); setUrlParsed(parseMapsUrl(d.expanded ?? raw)); })
        .catch(() => { setUrlExpanding(false); setUrlParsed(null); });
    } else {
      setUrlExpanding(false); setUrlParsed(parseMapsUrl(raw));
    }
  }, [mapsUrl]);

  const selectedEngines = (Object.keys(engines) as Engine[]).filter(e => engines[e]);
  const cleanKeywords = keywords.map(k => k.trim()).filter(Boolean);
  const needsKeyword = engines.maps || engines.ai;

  async function postJson<R>(path: string, body: unknown): Promise<R> {
    const res = await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error || `Request failed (${res.status})`);
    return data as R;
  }

  async function startAudit() {
    setError('');
    if (name.trim().length < 2) { setError('Enter the business name.'); return; }
    if (selectedEngines.length === 0) { setError('Select at least one thing to audit.'); return; }
    if (needsKeyword && cleanKeywords.length === 0) { setError('Enter at least one keyword for Maps / AI.'); return; }

    if (urlParsed) {
      // URL confirmed the exact business — skip search entirely.
      void runAudit({ place_id: urlParsed.placeId, name: name.trim(), address: '', lat: urlParsed.lat, lng: urlParsed.lng, rating: '0', reviews: 0 });
      return;
    }

    setPhase('searching');
    try {
      const { results } = await postJson<{ results: Candidate[] }>('/api/quick-audit/search', { name, proximity: city });
      if (results.length === 0) {
        setError('No Google Business listing found. Try adding the city, or check the spelling.');
        setPhase('form'); return;
      }
      if (results.length === 1) { void runAudit(results[0]); }
      else { setCandidates(results); setPhase('picking'); }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed.'); setPhase('form');
    }
  }

  const pollJob = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/quick-audit/job/${encodeURIComponent(id)}`);
      const data = await res.json() as AuditJob & { error?: string };
      if (data.error) throw new Error(data.error);
      setJob(data);
      // Keep polling while the audit runs OR a full GBP report is being generated.
      const reportPending = data.gbp_report?.status === 'pending';
      if (data.audit.status !== 'running' && !reportPending) { return; }
      const cap = reportPending ? REPORT_MAX_POLL_MS : MAX_POLL_MS;
      if (Date.now() - pollStart.current > cap) { return; }
    } catch (e) {
      if (pollTimer.current) clearTimeout(pollTimer.current);
      setError(e instanceof Error ? e.message : 'Failed while fetching the audit.');
      setPhase('error'); return;
    }
    pollTimer.current = setTimeout(() => pollJob(id), POLL_MS);
  }, []);

  async function requestGbpReport(id: string): Promise<void> {
    await postJson('/api/quick-audit/' + encodeURIComponent(id) + '/gbp-report', {});
    pollStart.current = Date.now();
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollJob(id); // resume polling for the report to land
  }

  async function runAudit(c: Candidate) {
    setError(''); setPhase('auditing'); setJob(null);
    pollStart.current = Date.now();
    try {
      const { audit_id } = await postJson<{ audit_id: string }>('/api/quick-audit/start', {
        place_id: c.place_id, business_name: c.name, address: c.address,
        lat: c.lat, lng: c.lng, location_area: city || c.address,
        market, keyword_language: lang, keywords: cleanKeywords, engines: selectedEngines,
      });
      setPhase('results');
      pollJob(audit_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the audit.'); setPhase('error');
    }
  }

  async function openFromHistory(id: string) {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    setTab('new'); setPhase('results'); setError(''); setJob(null);
    pollStart.current = Date.now();
    pollJob(id);
  }

  function reset() {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    setPhase('form'); setCandidates([]); setJob(null); setError('');
    setMapsUrl(''); setUrlParsed(null); setUrlExpanding(false);
  }

  const isMobile = useIsMobile();
  return (
    <div style={{
      background: T.bgSurface, fontFamily: sharedStyles.fontBody, color: T.fgPrimary,
      height: isMobile ? 'auto' : '100vh',
      minHeight: isMobile ? '100vh' : undefined,
      overflowY: isMobile ? undefined : 'auto',
    }}>
      <TopBar tab={tab} setTab={(t) => { setTab(t); }} />
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px 48px' }}>
        {tab === 'history' ? (
          <HistoryView onOpen={openFromHistory} />
        ) : (
          <>
            {(phase === 'form' || phase === 'searching') && (
              <FormCard
                name={name} setName={setName} city={city} setCity={setCity}
                mapsUrl={mapsUrl} setMapsUrl={setMapsUrl}
                urlParsed={urlParsed} urlExpanding={urlExpanding}
                market={market} setMarket={setMarket} lang={lang} setLang={setLang}
                keywords={keywords} setKeywords={setKeywords}
                engines={engines} setEngines={setEngines}
                needsKeyword={needsKeyword} busy={phase === 'searching'}
                error={error} onSubmit={startAudit}
              />
            )}
            {phase === 'picking' && (
              <PickerCard candidates={candidates} onPick={runAudit} onCancel={reset} />
            )}
            {(phase === 'auditing' || phase === 'results') && (
              <ResultsView job={job} onReset={reset} onGenerateGbp={requestGbpReport} />
            )}
            {phase === 'error' && (
              <Card><ErrorNote text={error} /><button style={{ ...primaryBtn(false), marginTop: 8 }} onClick={reset}>Start over</button></Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Top bar ──────────────────────────────────────────────────────────────────
function TopBar({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <div style={{ background: T.bgElevated, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/admin" style={{ fontSize: 13, color: T.fgSecondary, textDecoration: 'none', fontWeight: 600 }}>← Clients</Link>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 18, fontWeight: 800 }}>🎯 Quick Audit</div>
        <div style={{ flex: 1 }} />
        {(['new', 'history'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '7px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
            border: `1px solid ${tab === t ? T.brandOrange : T.border}`,
            background: tab === t ? 'rgba(232,80,26,0.06)' : 'transparent',
            color: tab === t ? T.brandOrange : T.fgSecondary,
          }}>{t === 'new' ? 'New audit' : 'History'}</button>
        ))}
      </div>
    </div>
  );
}

// ── Input form ─────────────────────────────────────────────────────────────
function FormCard(p: {
  name: string; setName: (s: string) => void; city: string; setCity: (s: string) => void;
  mapsUrl: string; setMapsUrl: (s: string) => void;
  urlParsed: ParsedMapsUrl | null; urlExpanding: boolean;
  market: 'SG' | 'MY'; setMarket: (m: 'SG' | 'MY') => void; lang: 'en' | 'zh' | 'ms'; setLang: (l: 'en' | 'zh' | 'ms') => void;
  keywords: string[]; setKeywords: (k: string[]) => void;
  engines: Record<Engine, boolean>; setEngines: (e: Record<Engine, boolean>) => void;
  needsKeyword: boolean; busy: boolean; error: string; onSubmit: () => void;
}) {
  const setKw = (i: number, v: string) => { const next = [...p.keywords]; next[i] = v; p.setKeywords(next); };
  const toggle = (e: Engine) => p.setEngines({ ...p.engines, [e]: !p.engines[e] });

  const urlStatus: 'ok' | 'bad' | 'expanding' | 'empty' =
    p.urlExpanding ? 'expanding' :
    !p.mapsUrl.trim() ? 'empty' :
    p.urlParsed ? 'ok' : 'bad';

  const urlHint =
    urlStatus === 'expanding' ? 'Expanding short link…' :
    urlStatus === 'ok' ? `✓ Place confirmed — search skipped` :
    urlStatus === 'bad' ? '⚠ Could not read this link. Try the full Google Maps URL (open in browser → copy link), or leave blank and search by name.' :
    null;

  const urlHintColor = urlStatus === 'ok' ? T.green : T.brandAmber;

  return (
    <Card>
      <h2 style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Audit a prospect</h2>
      <p style={{ fontSize: 13.5, color: T.fgSecondary, margin: '0 0 20px' }}>
        One-shot snapshot of where a business stands today. Pick which checks to run.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
        <Field label="Business name"><input style={inputStyle} value={p.name} onChange={e => p.setName(e.target.value)} placeholder="MX Clinic Signature" disabled={p.busy} /></Field>
        <Field label="City / area"><input style={inputStyle} value={p.city} onChange={e => p.setCity(e.target.value)} placeholder="Johor Bahru" disabled={p.busy} /></Field>
      </div>
      <Field label="Google Maps URL (optional — paste for guaranteed accuracy)">
        <input
          style={{ ...inputStyle, borderColor: urlStatus === 'ok' ? T.green : urlStatus === 'bad' ? T.brandAmber : undefined }}
          value={p.mapsUrl} onChange={e => p.setMapsUrl(e.target.value)}
          placeholder="https://maps.app.goo.gl/… or https://www.google.com/maps/place/…"
          disabled={p.busy}
        />
        {urlHint && (
          <div style={{ fontSize: 12, color: urlHintColor, marginTop: 5, lineHeight: 1.5 }}>{urlHint}</div>
        )}
        {urlStatus === 'empty' && (
          <div style={{ fontSize: 11.5, color: T.fgMuted, marginTop: 5 }}>
            In Google Maps: tap the business → Share → Copy link. Supports full URLs and short links.
          </div>
        )}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Market">
          <select style={inputStyle} value={p.market} onChange={e => p.setMarket(e.target.value as 'SG' | 'MY')} disabled={p.busy}>
            <option value="SG">Singapore</option><option value="MY">Malaysia</option>
          </select>
        </Field>
        <Field label="Keyword language">
          <select style={inputStyle} value={p.lang} onChange={e => p.setLang(e.target.value as 'en' | 'zh' | 'ms')} disabled={p.busy}>
            <option value="en">English</option><option value="zh">Chinese</option><option value="ms">Malay</option>
          </select>
        </Field>
      </div>
      <Field label={`Keywords (up to 3${p.needsKeyword ? '' : ' — optional for GBP-only'})`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {p.keywords.map((kw, i) => (
            <input key={i} style={inputStyle} value={kw} onChange={e => setKw(i, e.target.value)} disabled={p.busy}
              placeholder={i === 0 ? 'skin specialist johor bahru (primary)' : `keyword ${i + 1} (optional)`} />
          ))}
        </div>
        <div style={{ fontSize: 11.5, color: T.fgMuted, marginTop: 6 }}>Both the Maps grid and AI Visibility run <b>every</b> keyword you enter — each Maps keyword is a separate 3×3 scan (~9 credits each).</div>
      </Field>
      <Field label="What to audit">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(['maps', 'ai', 'gbp'] as Engine[]).map(e => (
            <label key={e} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer',
              border: `1px solid ${p.engines[e] ? T.cyan : T.border}`, borderRadius: 10,
              background: p.engines[e] ? 'rgba(18,181,196,0.05)' : T.bgSurface,
            }}>
              <input type="checkbox" checked={p.engines[e]} onChange={() => toggle(e)} disabled={p.busy} />
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{ENGINE_LABEL[e]}</div>
                <div style={{ fontSize: 11.5, color: T.fgMuted }}>{ENGINE_DESC[e]}</div>
              </div>
            </label>
          ))}
        </div>
      </Field>
      {p.error && <ErrorNote text={p.error} />}
      <button style={primaryBtn(p.busy)} onClick={p.onSubmit} disabled={p.busy}>
        {p.busy ? (p.urlParsed ? 'Starting audit…' : 'Finding business…') : (p.urlParsed ? 'Start Audit' : 'Run Quick Audit')}
      </button>
    </Card>
  );
}
const ENGINE_DESC: Record<Engine, string> = {
  maps: '3×3 Google Maps geo-grid ranking · ~2 min · ~9 credits per keyword',
  ai: 'ChatGPT + Gemini "best in <city>" visibility',
  gbp: 'Rating, review velocity & quality snapshot',
};

// ── Candidate picker ─────────────────────────────────────────────────────────
function PickerCard({ candidates, onPick, onCancel }: { candidates: Candidate[]; onPick: (c: Candidate) => void; onCancel: () => void }) {
  return (
    <Card>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Which business is it?</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {candidates.map(c => (
          <button key={c.place_id} onClick={() => onPick(c)} style={candidateCard}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</div>
            <div style={{ fontSize: 12.5, color: T.fgSecondary, marginTop: 2 }}>{c.address}</div>
            <div style={{ fontSize: 12, color: T.fgMuted, marginTop: 4 }}>★ {Number(c.rating).toFixed(1)} · {c.reviews} reviews</div>
          </button>
        ))}
      </div>
      <button style={{ ...ghostBtn, marginTop: 16 }} onClick={onCancel}>← Start over</button>
    </Card>
  );
}

// ── Results ────────────────────────────────────────────────────────────────
function ResultsView({ job, onReset, onGenerateGbp }: { job: AuditJob | null; onReset: () => void; onGenerateGbp: (id: string) => Promise<void> }) {
  if (!job) return <Card><Spinner label="Starting your audit…" /></Card>;
  const { audit, results } = job;
  const byEngine = new Map(results.map(r => [r.engine, r]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{audit.business_name}</div>
            <div style={{ fontSize: 13, color: T.fgSecondary, marginTop: 2 }}>{audit.address || audit.location_area} · {audit.market}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {audit.keywords.map(k => <Chip key={k}>{k}</Chip>)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <StatusBadge status={audit.status} />
            <button onClick={onReset} style={ghostBtn}>New audit</button>
          </div>
        </div>
      </Card>

      {audit.engines.map(engine => {
        const r = byEngine.get(engine);
        return (
          <EngineSection key={engine} engine={engine} result={r} place_id={audit.place_id} business={audit.business_name}
            auditId={audit.id} gbpReport={job.gbp_report} onGenerateGbp={onGenerateGbp} />
        );
      })}
    </div>
  );
}

function EngineSection({ engine, result, place_id, business, auditId, gbpReport, onGenerateGbp }: {
  engine: Engine; result?: EngineResult; place_id: string; business: string;
  auditId: string; gbpReport: GbpReportMeta | null; onGenerateGbp: (id: string) => Promise<void>;
}) {
  const status = result?.status ?? 'pending';
  // Maps streams in keyword-by-keyword: show the grid as soon as the FIRST scan
  // lands, even while later keywords are still scanning.
  const mapsScans = engine === 'maps' ? (result?.data as MapsData | undefined) : undefined;
  const mapsHasData = !!mapsScans && normalizeMaps(mapsScans).scans.length > 0;
  const showSpinner = status === 'running' && !(engine === 'maps' && mapsHasData);
  const hasBody = status === 'done' || (engine === 'maps' && mapsHasData);

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hasBody ? 16 : 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>{ENGINE_LABEL[engine]}</div>
        <StatusBadge status={status} small />
      </div>
      {status === 'pending' && <Spinner label="Queued…" />}
      {showSpinner && <Spinner label={engine === 'maps' ? 'Scanning the geo-grid (~2 min)…' : engine === 'gbp' ? 'Pulling reviews…' : 'Asking the AI models…'} />}
      {status === 'error' && <ErrorNote text={result?.error || 'This check failed.'} />}

      {engine === 'maps' && mapsHasData && <MapsResult data={result!.data as MapsData} place_id={place_id} business={business} />}
      {engine === 'ai' && status === 'done' && <AiResult data={result!.data as AiData} />}
      {engine === 'gbp' && status === 'done' && <GbpResult data={result!.data as GbpData} />}

      {/* The full AI optimization report pulls its own data, so it's available
          even when the lightweight snapshot above errored or is still running. */}
      {engine === 'gbp' && status !== 'pending' && (
        <GbpReportBlock auditId={auditId} report={gbpReport} onGenerate={onGenerateGbp} />
      )}
    </Card>
  );
}

// Full AI GBP report — request button → copyable Claude command → iframe render.
function GbpReportBlock({ auditId, report, onGenerate }: { auditId: string; report: GbpReportMeta | null; onGenerate: (id: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const command = `Generate the Quick Audit GBP report for ${auditId}`;
  const status = report?.status ?? 'none';

  async function generate() {
    setBusy(true);
    try { await onGenerate(auditId); } finally { setBusy(false); }
  }
  function copy() { navigator.clipboard?.writeText(command).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }

  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px dashed ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>Full AI optimization report</div>
        {status !== 'none' && <StatusBadge status={status} small />}
      </div>

      {status === 'none' && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12.5, color: T.fgSecondary, marginBottom: 10 }}>
            The deep report (competitor reverse-engineering + field-by-field actions) is written by Claude, not the server. Request it, then run the command in Claude.
          </div>
          <button style={{ ...primaryBtn(busy), width: 'auto', padding: '10px 18px', marginTop: 0 }} onClick={generate} disabled={busy}>
            {busy ? 'Requesting…' : 'Generate full GBP report'}
          </button>
        </div>
      )}

      {(status === 'pending') && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12.5, color: T.fgSecondary, marginBottom: 8 }}>
            Run this in Claude (Cowork) to generate the report — it’ll appear here automatically when done:
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <code style={{ flex: 1, fontFamily: sharedStyles.fontMono, fontSize: 12.5, background: T.bgOverlay, border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', wordBreak: 'break-all' }}>{command}</code>
            <button style={ghostBtn} onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}><Spinner label="Waiting for the report…" /></div>
        </div>
      )}

      {status === 'error' && (
        <div style={{ marginTop: 10 }}>
          <ErrorNote text={report?.error || 'Report generation failed.'} />
          <button style={{ ...ghostBtn, marginTop: 10 }} onClick={generate} disabled={busy}>Try again</button>
        </div>
      )}

      {status === 'ready' && <GbpReportFrame auditId={auditId} summary={report?.summary} score={report?.score} onRegenerate={generate} busy={busy} />}
    </div>
  );
}

function GbpReportFrame({ auditId, summary, score, onRegenerate, busy }: { auditId: string; summary: string | null | undefined; score: number | null | undefined; onRegenerate: () => void; busy: boolean }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/quick-audit/' + encodeURIComponent(auditId) + '/gbp-report');
        const data = await res.json() as { html?: string | null; error?: string };
        if (data.error) throw new Error(data.error);
        setHtml(data.html ?? '');
      } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load the report.'); }
    })();
  }, [auditId]);
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        {score != null && <Stat label="GBP Score" value={`${Math.round(score)}/100`} color={T.green} sub="AI optimization" />}
        {summary && <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: T.fgSecondary }}>{summary}</div>}
        <button style={ghostBtn} onClick={onRegenerate} disabled={busy}>{busy ? 'Requesting…' : 'Regenerate'}</button>
      </div>
      {error && <ErrorNote text={error} />}
      {html != null && !error && (
        <iframe title="GBP report" srcDoc={html} style={{ width: '100%', height: 720, border: `1px solid ${T.border}`, borderRadius: 12, background: '#fff' }} />
      )}
      {html == null && !error && <Spinner label="Loading report…" />}
    </div>
  );
}

// Accept both the new multi-keyword shape and the legacy single-scan payload.
function normalizeMaps(data: MapsData): { scans: MapsScan[]; pending: { keyword: string; report_key: string }[]; errors: string[] } {
  if (Array.isArray(data.scans)) {
    return { scans: data.scans, pending: data.pending ?? [], errors: data.errors ?? [] };
  }
  // Legacy: the whole object IS a single scan.
  if (data.report_key && Array.isArray(data.grid)) {
    return { scans: [{ ...(data as unknown as MapsScan), keyword: data.keyword ?? '' }], pending: [], errors: [] };
  }
  return { scans: [], pending: data.pending ?? [], errors: data.errors ?? [] };
}

// ── Maps result (keyword switcher + Leaflet map + rankings panel) ────────────
function MapsResult({ data, place_id, business }: { data: MapsData; place_id: string; business: string }) {
  const isMobile = useIsMobile();
  const { scans, pending, errors } = normalizeMaps(data);

  const [selectedKw, setSelectedKw] = useState<string>(scans[0]?.keyword ?? '');
  // Keep the selection valid as more keyword scans stream in.
  useEffect(() => {
    if (scans.length && !scans.find(s => s.keyword === selectedKw)) setSelectedKw(scans[0].keyword);
  }, [scans, selectedKw]);

  const sel = scans.find(s => s.keyword === selectedKw) ?? scans[0];
  // Reset the clicked grid point when switching keyword (center is a safe default).
  const [selectedPt, setSelectedPt] = useState<number>(0);
  useEffect(() => { if (sel) setSelectedPt(Math.floor(sel.grid.length / 2)); }, [sel?.report_key]);

  if (!sel) return null;
  const total = sel.grid.length || 9;
  const foundIn = Number(sel.found_in);
  const blind = Number.isFinite(foundIn) ? Math.max(0, total - foundIn) : null;
  const solvNum = Number(sel.solv);
  const arpNum = Number(sel.arp);
  const point = sel.grid[selectedPt];
  const showSwitcher = scans.length + pending.length > 1;

  return (
    <div>
      {showSwitcher && (
        <MapsKeywordSwitcher
          scans={scans} pending={pending} selected={selectedKw} onSelect={setSelectedKw}
        />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="Avg Rank Position" value={Number.isFinite(arpNum) ? arpNum.toFixed(2) : '—'} color={T.green} sub={`Across ${total} points`} />
        <Stat label="Share of Local Voice" value={Number.isFinite(solvNum) ? `${solvNum.toFixed(1)}%` : '—'} color={T.cyan} sub="In Map Pack" />
        <Stat label="Found In" value={Number.isFinite(foundIn) ? `${foundIn}/${total}` : '—'} color={T.yellow} sub={Number.isFinite(foundIn) ? `${Math.round((foundIn / total) * 100)}% coverage` : '—'} />
        <Stat label="Blind Spots" value={blind != null ? String(blind) : '—'} color={T.magenta} sub="Not in Map Pack" />
      </div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}` }}>
          <QAMap scan={sel} onSelect={setSelectedPt} />
        </div>
        {point && <RankingsPanel point={point} youPlaceId={place_id} youName={business} />}
      </div>
      {errors.length > 0 && (
        <div style={{ fontSize: 11.5, color: T.brandAmber, marginTop: 12 }}>
          Some keywords couldn’t be scanned: {errors.join(' · ')}
        </div>
      )}
    </div>
  );
}

// Horizontal, clickable keyword pills — switch which keyword's grid is shown.
// Pending (still-scanning) keywords appear as a spinner pill.
function MapsKeywordSwitcher({ scans, pending, selected, onSelect }: {
  scans: MapsScan[]; pending: { keyword: string; report_key: string }[]; selected: string; onSelect: (k: string) => void;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.fgMuted, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        Keywords
        <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: T.cyan, background: 'rgba(18,181,196,0.10)', border: `1px solid ${T.cyan}`, borderRadius: 10, padding: '1px 8px' }}>👆 click to switch</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {scans.map((s, i) => {
          const active = s.keyword === selected;
          const solv = Number(s.solv);
          return (
            <button key={s.keyword || i} onClick={() => onSelect(s.keyword)} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer',
              borderRadius: 999, border: active ? `2px solid ${T.cyan}` : `1px solid ${T.border}`,
              background: active ? 'rgba(18,181,196,0.07)' : '#fff',
              fontWeight: active ? 700 : 600, fontSize: 13, color: active ? T.fgPrimary : T.fgSecondary,
              fontFamily: sharedStyles.fontBody,
            }}>
              <span style={{ width: 18, height: 18, borderRadius: 6, background: active ? T.cyan : T.bgOverlay, color: active ? '#fff' : T.fgMuted, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
              <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.keyword || '(primary)'}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.cyan, fontFamily: sharedStyles.fontMono }}>{Number.isFinite(solv) ? `${solv.toFixed(0)}%` : '—'}</span>
            </button>
          );
        })}
        {pending.map((p, i) => (
          <span key={`p${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 999, border: `1px dashed ${T.border}`, background: T.bgSurface, fontSize: 13, color: T.fgMuted }}>
            <span style={{ width: 14, height: 14, border: `2px solid ${T.border}`, borderTopColor: T.brandOrange, borderRadius: '50%', animation: 'qa-spin 0.9s linear infinite' }} />
            <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.keyword || '…'}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function QAMap({ scan: report, onSelect }: { scan: MapsScan; onSelect: (i: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const centerLat = report.center?.lat || report.grid[0]?.lat || 0;
    const centerLng = report.center?.lng || report.grid[0]?.lng || 0;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const map = L.map(containerRef.current, { center: [centerLat, centerLng], zoom: 12, scrollWheelZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    const bounds = L.latLngBounds([]);
    report.grid.forEach(p => {
      const rc = rankColor(p.rank);
      const bg = p.rank > 0 ? rc.bg : '#9aa0a6';
      const fg = p.rank > 0 ? rc.color : '#fff';
      const html = `<div style="width:30px;height:30px;border-radius:50%;background:${bg};display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:700;color:${fg};box-shadow:0 2px 6px rgba(0,0,0,0.2),0 0 0 2px rgba(255,255,255,0.85);cursor:pointer;">${p.rank > 20 ? '20+' : p.rank > 0 ? p.rank : 'X'}</div>`;
      const icon = L.divIcon({ className: '', html, iconSize: [30, 30], iconAnchor: [15, 15] });
      const marker = L.marker([p.lat, p.lng], { icon }).addTo(map);
      marker.on('click', () => onSelect(p.index));
      bounds.extend([p.lat, p.lng]);
    });
    if (report.grid.length > 0) map.fitBounds(bounds, { padding: [40, 40] });
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.report_key]);
  return <div ref={containerRef} style={{ height: 420, width: '100%' }} />;
}

function RankingsPanel({ point, youPlaceId, youName }: { point: MapsGridPoint; youPlaceId: string; youName: string }) {
  const inTop = point.results.some(c => c.place_id === youPlaceId);
  return (
    <div style={{ width: 360, maxWidth: '100%', flexShrink: 0, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12 }}>
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}`, fontSize: 13, fontWeight: 700 }}>Rankings at point #{point.index + 1}</div>
      <div style={{ padding: '12px 14px' }}>
        {point.results.length === 0 ? <div style={{ fontSize: 12, color: T.fgMuted }}>No business in the top 20 here.</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {point.results.map(c => <RankRow key={c.place_id} rank={c.rank} name={c.name} rating={c.rating} reviews={c.reviews} isYou={c.place_id === youPlaceId} />)}
            {!inTop && (point.rank > 0
              ? <RankRow rank={point.rank} name={youName} rating={null} reviews={null} isYou />
              : <div style={{ marginTop: 6, padding: '8px 10px', background: 'rgba(232,80,26,0.06)', border: `1px solid ${T.magenta}`, borderRadius: 8, fontSize: 12, color: T.magenta }}>Not in the top 20 here.</div>)}
          </div>
        )}
      </div>
    </div>
  );
}
function RankRow({ rank, name, rating, reviews, isYou }: { rank: number; name: string; rating: number | null; reviews: number | null; isYou: boolean }) {
  const rc = rankColor(rank);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: isYou ? 'rgba(18,181,196,0.06)' : T.bgSurface, border: `1px solid ${isYou ? T.cyan : T.border}`, borderRadius: 9 }}>
      <div style={{ width: 30, height: 30, borderRadius: '50%', background: rank > 0 ? rc.bg : '#9aa0a6', color: rank > 0 ? rc.color : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: sharedStyles.fontMono, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{rank > 0 ? rank : 'X'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          {isYou && <span style={{ fontSize: 9, fontWeight: 700, color: T.cyan, background: 'rgba(18,181,196,0.12)', border: `1px solid ${T.cyan}`, borderRadius: 8, padding: '1px 5px' }}>YOU</span>}
        </div>
        {rating != null && <div style={{ fontSize: 11, color: T.fgMuted }}>★ {rating.toFixed(1)}{reviews != null ? ` (${reviews})` : ''}</div>}
      </div>
    </div>
  );
}

// ── AI Visibility result ─────────────────────────────────────────────────────
function AiResult({ data }: { data: AiData }) {
  const isMobile = useIsMobile();
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="AI Visibility" value={`${data.visibility_pct}%`} color={T.cyan} sub={`${data.mentioned_count}/${data.total} answers`} />
        <Stat label="Best Rank" value={data.best_rank != null ? `#${data.best_rank}` : '—'} color={T.green} sub="When recommended" />
        <Stat label="Models" value={String(data.platforms.length)} color={T.purple} sub={data.platforms.map(labelFor).join(' · ')} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.scans.map((s, i) => (
          <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', background: T.bgSurface }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: platColor(s.platform), borderRadius: 6, padding: '2px 7px' }}>{s.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{s.keyword}</span>
              <div style={{ flex: 1 }} />
              {s.mentioned
                ? <span style={{ fontSize: 11.5, fontWeight: 700, color: T.green }}>{s.rank != null ? `Recommended #${s.rank}` : 'Mentioned'}</span>
                : <span style={{ fontSize: 11.5, fontWeight: 700, color: T.magenta }}>Not mentioned</span>}
            </div>
            {s.competitors.length > 0 && (
              <div style={{ fontSize: 11.5, color: T.fgMuted, marginTop: 6 }}>
                Model recommended: {s.competitors.slice(0, 6).join(', ')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
function labelFor(p: string): string { return p === 'chatgpt' ? 'ChatGPT' : p === 'gemini' ? 'Gemini' : p; }
function platColor(p: string): string { return p === 'chatgpt' ? T.chatgpt : p === 'gemini' ? T.gemini : T.fgSecondary; }

// ── GBP snapshot result ──────────────────────────────────────────────────────
function GbpResult({ data }: { data: GbpData }) {
  const isMobile = useIsMobile();
  const maxMonth = Math.max(1, ...data.monthly.map(m => m.count));
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="Rating" value={data.rating.value != null ? `★ ${data.rating.value.toFixed(1)}` : '—'} color={T.brandAmber} sub={`${data.total_reviews ?? '—'} reviews`} />
        <Stat label="Effective Reviews" value={`${data.effective.pct}%`} color={T.green} sub={`${data.effective.effective_count}/${data.sample_size} sampled`} />
        <Stat label="With Photos" value={`${data.effective.photo_pct}%`} color={T.cyan} sub="Of sampled reviews" />
        <Stat label="Last 30 days" value={String(data.recent.last_30d)} color={T.purple} sub={`${data.recent.last_14d} in 14d`} />
      </div>
      {data.monthly.length > 0 && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px', background: T.bgSurface }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.fgMuted, marginBottom: 10 }}>New reviews / month</div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', height: 90 }}>
            {[...data.monthly].reverse().map(m => (
              <div key={m.ym} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.fgSecondary }}>{m.count}</div>
                <div style={{ width: '100%', maxWidth: 48, height: `${Math.round((m.count / maxMonth) * 60)}px`, minHeight: 3, background: T.brandGradient, borderRadius: 4 }} />
                <div style={{ fontSize: 10, color: T.fgMuted }}>{m.ym.slice(2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ fontSize: 11.5, color: T.fgMuted, marginTop: 12 }}>
        Quality breakdown — photos {data.effective.photo_pct}% · substantive text {data.effective.quality_text_pct}% · junk {data.effective.junk_pct}%
      </div>
    </div>
  );
}

// ── History ──────────────────────────────────────────────────────────────────
function HistoryView({ onOpen }: { onOpen: (id: string) => void }) {
  const [audits, setAudits] = useState<HistoryAudit[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/quick-audit/history');
        const data = await res.json() as { audits?: HistoryAudit[]; error?: string };
        if (data.error) throw new Error(data.error);
        setAudits(data.audits ?? []);
      } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load history.'); }
    })();
  }, []);
  if (error) return <Card><ErrorNote text={error} /></Card>;
  if (!audits) return <Card><Spinner label="Loading history…" /></Card>;
  if (audits.length === 0) return <Card><div style={{ fontSize: 13.5, color: T.fgSecondary, textAlign: 'center', padding: '24px 0' }}>No audits yet. Run your first one from the “New audit” tab.</div></Card>;
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {audits.map(a => (
          <button key={a.id} onClick={() => onOpen(a.id)} style={{ ...candidateCard, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{a.business_name}</div>
              <div style={{ fontSize: 12, color: T.fgMuted, marginTop: 2 }}>
                {a.location_area || '—'} · {a.market} · {new Date(a.created_at).toLocaleDateString()} {new Date(a.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 5, flexWrap: 'wrap' }}>{a.keywords.map(k => <Chip key={k}>{k}</Chip>)}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {a.results.map(r => (
                <div key={r.engine} title={`${ENGINE_LABEL[r.engine]}: ${r.status}`} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9.5, color: T.fgMuted, textTransform: 'uppercase', fontWeight: 700 }}>{r.engine}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: r.status === 'done' ? T.fgPrimary : r.status === 'error' ? T.magenta : T.fgMuted }}>
                    {r.status === 'done' ? fmtScore(r.engine, r.score) : r.status === 'error' ? '✕' : '…'}
                  </div>
                </div>
              ))}
            </div>
            <StatusBadge status={a.status} small />
          </button>
        ))}
      </div>
    </Card>
  );
}
function fmtScore(engine: Engine, score: number | null): string {
  if (score == null) return '—';
  if (engine === 'maps') return score.toFixed(1);       // ARP
  return `${Math.round(score)}%`;                        // ai visibility / gbp effective
}

// ── Primitives ───────────────────────────────────────────────────────────────
function Card({ children }: { children: ReactNode }) {
  return <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>{children}</div>;
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div style={{ marginBottom: 14 }}><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.fgSecondary, marginBottom: 6 }}>{label}</label>{children}</div>;
}
function Stat({ label, value, color, sub }: { label: string; value: string; color: string; sub: string }) {
  return (
    <div style={{ background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 10, color: T.fgMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: sharedStyles.fontMono, fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}
function Chip({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 11.5, fontWeight: 600, color: T.fgSecondary, background: T.bgOverlay, borderRadius: 999, padding: '3px 10px' }}>{children}</span>;
}
function StatusBadge({ status, small }: { status: string; small?: boolean }) {
  const map: Record<string, { c: string; t: string }> = {
    running: { c: T.cyan, t: 'Running' }, pending: { c: T.fgMuted, t: 'Queued' },
    done: { c: T.green, t: 'Done' }, partial: { c: T.brandAmber, t: 'Partial' }, error: { c: T.magenta, t: 'Error' },
  };
  const s = map[status] ?? map.running;
  return <span style={{ fontSize: small ? 10 : 11, fontWeight: 700, color: s.c, background: `${s.c}14`, border: `1px solid ${s.c}`, borderRadius: 999, padding: small ? '1px 8px' : '3px 11px' }}>{s.t}</span>;
}
function Spinner({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', color: T.fgSecondary, fontSize: 13.5 }}>
      <div style={{ width: 22, height: 22, border: `3px solid ${T.border}`, borderTopColor: T.brandOrange, borderRadius: '50%', animation: 'qa-spin 0.9s linear infinite' }} />
      {label}
      <style>{'@keyframes qa-spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}
function ErrorNote({ text }: { text: string }) {
  return <div style={{ background: '#fce8e6', color: '#c5221f', border: '1px solid #f4c7c3', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>{text}</div>;
}

const inputStyle: CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '11px 14px', fontSize: 14.5, border: `1px solid ${T.border}`, borderRadius: 10, outline: 'none', fontFamily: sharedStyles.fontBody, background: '#fff' };
function primaryBtn(disabled: boolean): CSSProperties {
  return { width: '100%', padding: '13px', fontSize: 15, fontWeight: 700, color: '#fff', border: 'none', borderRadius: 10, cursor: disabled ? 'default' : 'pointer', marginTop: 6, background: disabled ? T.fgMuted : T.brandGradient, opacity: disabled ? 0.8 : 1 };
}
const ghostBtn: CSSProperties = { padding: '9px 16px', fontSize: 13.5, fontWeight: 600, color: T.fgSecondary, background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 10, cursor: 'pointer' };
const candidateCard: CSSProperties = { textAlign: 'left', padding: '12px 14px', border: `1px solid ${T.border}`, borderRadius: 10, background: T.bgSurface, cursor: 'pointer', width: '100%' };
